import React, { useEffect, useRef } from "react";

function FitnessBackgroundCanvas({ activeMetric = "CARDIO", isDarkMode = true }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let animationFrameId;
    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
    };
    window.addEventListener("resize", handleResize);

    // Mouse coordinates for interactive HUD
    let mouseX = -100;
    let mouseY = -100;
    let isMouseOver = false;

    const handleMouseMove = (e) => {
      mouseX = e.clientX;
      mouseY = e.clientY;
      isMouseOver = true;
    };

    const handleMouseLeave = () => {
      isMouseOver = false;
    };

    // Click ripples array (Rep explosions)
    const REWARD_TEXTS = [
      "+65 KCAL BURN 🔥",
      "REP 12 COMPLETED! 💪",
      "HEART RATE: 154 BPM ❤️",
      "NEW PR SMASHED! ⚡",
      "STREAK +1 DAY 🏆",
      "HYDRATION +300ML 💧",
      "ZONE 4 MAX CARDIO 🚀",
      "WARRIOR FOCUS 100% 🎯",
    ];

    let ripples = [];
    const handleClick = (e) => {
      const target = e.target;
      if (target.closest && (target.closest(".fitness-card-container") || target.closest("button") || target.closest("input"))) {
        return;
      }

      const reward = REWARD_TEXTS[Math.floor(Math.random() * REWARD_TEXTS.length)];
      ripples.push({
        x: e.clientX,
        y: e.clientY,
        r: 6,
        maxR: 70,
        opacity: 0.95,
        text: reward,
      });
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseleave", handleMouseLeave);
    window.addEventListener("click", handleClick);

    // Rising Ember Particles
    const emberCount = 38;
    const embers = [];
    for (let i = 0; i < emberCount; i++) {
      embers.push({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() * 2.5 + 1.2,
        speedY: Math.random() * 0.9 + 0.35,
        speedX: (Math.random() - 0.5) * 0.45,
        opacity: Math.random() * 0.7 + 0.2,
        color: Math.random() > 0.4 ? "#ff6b35" : Math.random() > 0.5 ? "#ffa033" : "#ff3b30",
      });
    }

    // Floating Athlete Ticker Badges
    const floatingBadges = [
      { text: "🔥 AMANDEEP: 820 kcal • 5km Run", x: width * 0.06, y: height * 0.16, vx: -0.42, color: "#ff6b35" },
      { text: "⚡ WORKOUT STREAK: 18 Days Live", x: width * 0.74, y: height * 0.22, vx: -0.38, color: "#f59e0b" },
      { text: "❤️ AVG HEART RATE: 142 BPM (Zone 4)", x: width * 0.22, y: height * 0.82, vx: -0.46, color: "#ef4444" },
      { text: "🏆 TODAY'S LEADERBOARD: Rank #1", x: width * 0.82, y: height * 0.77, vx: -0.39, color: "#10b981" },
      { text: "💧 HYDRATION LEVEL: 2.8L / 3.0L", x: width * 0.52, y: height * 0.89, vx: -0.36, color: "#06b6d4" },
      { text: "🏋️ BENCH PRESS PR: 95 KG (New High)", x: width * 0.88, y: height * 0.14, vx: -0.44, color: "#8b5cf6" },
    ];

    // ECG cardiac wave state
    let ecgOffset = 0;
    let waveTime = 0;

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      // Background fill
      if (isDarkMode) {
        const bgGrad = ctx.createRadialGradient(
          width * 0.5, height * 0.1, 80,
          width * 0.5, height * 0.6, width * 0.8
        );
        bgGrad.addColorStop(0, "#190c2e");
        bgGrad.addColorStop(0.5, "#0b0617");
        bgGrad.addColorStop(1, "#030206");
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, width, height);
      } else {
        const bgGrad = ctx.createRadialGradient(
          width * 0.5, height * 0.1, 80,
          width * 0.5, height * 0.6, width * 0.8
        );
        bgGrad.addColorStop(0, "#f4f6ff");
        bgGrad.addColorStop(0.5, "#eef1fc");
        bgGrad.addColorStop(1, "#e5e9f8");
        ctx.fillStyle = bgGrad;
        ctx.fillRect(0, 0, width, height);
      }

      // 1. Futuristic Biometric Grid
      ctx.strokeStyle = isDarkMode ? "rgba(255, 107, 53, 0.05)" : "rgba(255, 107, 53, 0.08)";
      ctx.lineWidth = 1;

      const gridSpacingX = 80;
      for (let x = 0; x < width; x += gridSpacingX) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
        ctx.stroke();
      }

      const gridSpacingY = 60;
      ctx.font = "10px monospace";
      ctx.fillStyle = isDarkMode ? "rgba(255, 255, 255, 0.22)" : "rgba(0, 0, 0, 0.25)";
      ctx.textAlign = "right";

      for (let y = 60; y < height; y += gridSpacingY) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();

        const bpmValue = Math.round(180 - (y / height) * 110);
        ctx.fillText(`${bpmValue} BPM`, width - 16, y - 4);
      }

      // 2. Animated Calorie Burn Ambient Waves (Lower half)
      waveTime += 0.016;
      const waveBaseY = height * 0.68;

      ctx.save();
      ctx.beginPath();
      ctx.moveTo(0, height);
      for (let x = 0; x <= width; x += 15) {
        const y =
          waveBaseY +
          Math.sin(x * 0.005 + waveTime * 1.2) * 28 +
          Math.cos(x * 0.009 - waveTime * 0.8) * 18;
        ctx.lineTo(x, y);
      }
      ctx.lineTo(width, height);
      ctx.closePath();

      const waveGrad = ctx.createLinearGradient(0, waveBaseY - 40, 0, height);
      if (isDarkMode) {
        waveGrad.addColorStop(0, "rgba(255, 107, 53, 0.16)");
        waveGrad.addColorStop(0.5, "rgba(139, 92, 246, 0.08)");
        waveGrad.addColorStop(1, "rgba(3, 2, 6, 0.0)");
      } else {
        waveGrad.addColorStop(0, "rgba(255, 107, 53, 0.14)");
        waveGrad.addColorStop(0.5, "rgba(139, 92, 246, 0.06)");
        waveGrad.addColorStop(1, "rgba(240, 242, 255, 0.0)");
      }
      ctx.fillStyle = waveGrad;
      ctx.fill();
      ctx.restore();

      // 3. Live ECG Cardiac Heart Rate Pulse Line
      ecgOffset += 2.4;
      if (ecgOffset > width) ecgOffset = 0;

      const ecgY = height * 0.52;
      const ecgCycleWidth = 260; // wavelength of a single full heartbeat

      ctx.save();
      ctx.beginPath();
      ctx.lineWidth = 2.8;
      ctx.strokeStyle = isDarkMode ? "#ff6b35" : "#e0531c";
      ctx.shadowColor = "#ff6b35";
      ctx.shadowBlur = isDarkMode ? 14 : 6;

      for (let x = 0; x <= width; x += 4) {
        const cycleProgress = ((x + ecgOffset) % ecgCycleWidth) / ecgCycleWidth;
        let dy = 0;

        // P wave
        if (cycleProgress > 0.15 && cycleProgress < 0.22) {
          dy = -Math.sin(((cycleProgress - 0.15) / 0.07) * Math.PI) * 14;
        }
        // Q-R-S complex (sharp ventricular contraction)
        else if (cycleProgress >= 0.28 && cycleProgress < 0.30) {
          dy = ((cycleProgress - 0.28) / 0.02) * 12; // Q dip
        } else if (cycleProgress >= 0.30 && cycleProgress < 0.35) {
          const t = (cycleProgress - 0.30) / 0.05;
          dy = -Math.sin(t * Math.PI) * 65; // Massive R spike
        } else if (cycleProgress >= 0.35 && cycleProgress < 0.38) {
          dy = ((cycleProgress - 0.35) / 0.03) * 18; // S dip
        }
        // T wave (ventricular repolarization)
        else if (cycleProgress > 0.44 && cycleProgress < 0.56) {
          dy = -Math.sin(((cycleProgress - 0.44) / 0.12) * Math.PI) * 22;
        }

        const y = ecgY + dy;
        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.restore();

      // 4. Floating Rising Ember Particles
      embers.forEach((emb) => {
        emb.y -= emb.speedY;
        emb.x += emb.speedX;

        if (emb.y < -10) {
          emb.y = height + 10;
          emb.x = Math.random() * width;
        }

        ctx.save();
        ctx.beginPath();
        ctx.arc(emb.x, emb.y, emb.r, 0, Math.PI * 2);
        ctx.fillStyle = emb.color;
        ctx.shadowColor = emb.color;
        ctx.shadowBlur = 8;
        ctx.globalAlpha = emb.opacity;
        ctx.fill();
        ctx.restore();
      });

      // 5. Floating Live Athlete Badges
      floatingBadges.forEach((badge) => {
        badge.x += badge.vx;
        if (badge.x < -280) {
          badge.x = width + Math.random() * 80;
        }

        const pillW = 230;
        const pillH = 30;
        const rad = 15;

        ctx.save();
        ctx.fillStyle = isDarkMode ? "rgba(18, 11, 35, 0.88)" : "rgba(255, 255, 255, 0.9)";
        ctx.strokeStyle = isDarkMode ? "rgba(255, 107, 53, 0.35)" : "rgba(255, 107, 53, 0.35)";
        ctx.lineWidth = 1;

        ctx.beginPath();
        ctx.roundRect(badge.x, badge.y, pillW, pillH, rad);
        ctx.fill();
        ctx.stroke();

        ctx.font = "bold 11px Inter, sans-serif";
        ctx.fillStyle = isDarkMode ? "#f8fafc" : "#1e1b4b";
        ctx.textAlign = "left";
        ctx.fillText(badge.text, badge.x + 14, badge.y + 19);
        ctx.restore();
      });

      // 6. Interactive Mouse Biometric Scanner HUD
      if (isMouseOver && mouseX > 0 && mouseY > 0) {
        ctx.save();
        ctx.strokeStyle = isDarkMode ? "rgba(255, 107, 53, 0.45)" : "rgba(234, 88, 12, 0.45)";
        ctx.lineWidth = 1;
        ctx.setLineDash([5, 5]);

        // Crosshairs
        ctx.beginPath();
        ctx.moveTo(0, mouseY);
        ctx.lineTo(width, mouseY);
        ctx.stroke();

        ctx.beginPath();
        ctx.moveTo(mouseX, 0);
        ctx.lineTo(mouseX, height);
        ctx.stroke();

        ctx.setLineDash([]);

        // Reticle ring
        ctx.beginPath();
        ctx.arc(mouseX, mouseY, 16, 0, Math.PI * 2);
        ctx.strokeStyle = "#ff6b35";
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // Biometric Tag beside cursor
        const tagText = "❤️ 148 BPM • PEAK CARDIO ZONE";
        const tagW = 190;
        const tagH = 24;

        ctx.fillStyle = isDarkMode ? "rgba(24, 12, 42, 0.95)" : "rgba(255, 255, 255, 0.95)";
        ctx.strokeStyle = "#ff6b35";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(mouseX + 22, mouseY - 28, tagW, tagH, 6);
        ctx.fill();
        ctx.stroke();

        ctx.font = "bold 10px monospace";
        ctx.fillStyle = "#ff6b35";
        ctx.textAlign = "left";
        ctx.fillText(tagText, mouseX + 30, mouseY - 12);

        ctx.restore();
      }

      // 7. Click Energy Burst Shockwaves
      ripples.forEach((r, idx) => {
        r.r += 2.2;
        r.opacity -= 0.025;

        if (r.opacity <= 0) {
          ripples.splice(idx, 1);
          return;
        }

        ctx.save();
        ctx.beginPath();
        ctx.arc(r.x, r.y, r.r, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255, 107, 53, ${r.opacity})`;
        ctx.lineWidth = 2.5;
        ctx.stroke();

        ctx.font = "bold 12px Inter, sans-serif";
        ctx.fillStyle = `rgba(255, 107, 53, ${r.opacity})`;
        ctx.textAlign = "center";
        ctx.shadowColor = "#ff6b35";
        ctx.shadowBlur = 8;
        ctx.fillText(r.text, r.x, r.y - r.r - 6);
        ctx.restore();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseleave", handleMouseLeave);
      window.removeEventListener("click", handleClick);
    };
  }, [activeMetric, isDarkMode]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        zIndex: 0,
        pointerEvents: "auto",
        cursor: "crosshair",
        transition: "background 0.3s ease",
      }}
    />
  );
}

export default FitnessBackgroundCanvas;
